import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { NumberInput } from '../ui/NumberInput';
import { currentMonth, monthLabel } from './format';
import { InvoiceSettingsPage } from '../invoices/InvoiceSettingsPage';
import { LineRulesSettings } from '../invoices/LineRulesSettings';
import { INVOICE_SETTINGS_RESOURCE } from '../invoices/types';
import { KSEF_STATUS_RESOURCE, KsefStatusCard } from './settings/KsefStatusCard';
import { SummaryPage } from './SummaryPage';
import { TransportSettings } from './TransportSettings';
import { BANK_RESOURCE } from '../bank/types';
import { FixedCostPeriods, PaymentLabel } from './FixedCostStatus';
import {
  FINANCE_KEY,
  FIXED_RESOURCE,
  PERIODS,
  SETTINGS_RESOURCE,
  type FixedCost,
  type FixedPeriod,
} from './types';
import { SettlementsSettings } from '../settlements/SettlementsSettings';
import { PaymentsSettings } from '../payments/PaymentsSettings';
import { PAYMENTS_SETTINGS_RESOURCE } from '../payments/types';
import { SETTLEMENTS_SETTINGS_RESOURCE } from '../settlements/types';
import './finance.css';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

type Segment = 'zestawienie' | 'koszty-stale' | 'ustawienia';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);

// ---------- Koszty stałe ----------

type FixedDraft = { name: string; amount: string; period: FixedPeriod; fromMonth: string; toMonth: string; notes: string; matchRecipient: string; matchAccount: string; matchTitle: string };
const emptyDraft = (): FixedDraft => ({ name: '', amount: '', period: 'miesiecznie', fromMonth: currentMonth(), toMonth: '', notes: '', matchRecipient: '', matchAccount: '', matchTitle: '' });
const draftOf = (c: FixedCost): FixedDraft => ({
  name: c.name,
  amount: plnToInput(c.amountGrosze),
  period: c.period,
  fromMonth: c.fromMonth,
  toMonth: c.toMonth ?? '',
  notes: c.notes,
  matchRecipient: c.matchRecipient ?? '',
  matchAccount: c.matchAccount ?? '',
  matchTitle: c.matchTitle ?? '',
});
/** Owner 2026-10-24: hints for matching the outgoing transfers to the cost („Odbiorca / rachunek / słowo w tytule”). */
const HINTS = [
  { key: 'matchRecipient', label: 'Odbiorca przelewu', placeholder: 'np. Kowalski Nieruchomości' },
  { key: 'matchAccount', label: 'Rachunek odbiorcy', placeholder: 'np. PL60 1020 …' },
  { key: 'matchTitle', label: 'Słowo w tytule', placeholder: 'np. czynsz' },
] as const;

function FixedForm({ me, initial, onSave, onCancel, pending, error, label }: { me: Me; initial: FixedDraft; onSave: (body: Record<string, unknown>) => Promise<unknown>; onCancel?: () => void; pending: boolean; error: unknown; label: string }) {
  // rule 7a: the hints are the bank's data — shown and sent only with the statements' view (and the field visible)
  const hints = can(me, BANK_RESOURCE, 'view') ? HINTS.filter((h) => fieldVisible(me, FIXED_RESOURCE, h.key)) : [];
  const [d, setD] = useState(initial);
  const [local, setLocal] = useState('');
  function bodyOf(): Record<string, unknown> | null {
    let amountGrosze: number;
    try {
      amountGrosze = parsePln(d.amount);
    } catch (err) {
      return (setLocal((err as Error).message), null);
    }
    setLocal('');
    return {
      name: d.name.trim(),
      amountGrosze,
      period: d.period,
      fromMonth: d.fromMonth,
      toMonth: d.period === 'jednorazowo' || d.toMonth === '' ? null : d.toMonth,
      notes: d.notes.trim(),
      ...Object.fromEntries(hints.map((h) => [h.key, d[h.key].trim()])),
    };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = bodyOf();
    // the error is shown by the form (from the mutation)
    if (body) onSave(body).catch(() => undefined);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(d, initial),
    save: async (el) => {
      const body = bodyOf();
      if (!body || !validForm(el)) return false;
      await onSave(body);
      return true;
    },
  });
  const message = local || (error ? errorText(error, 'Nie udało się zapisać') : '');
  return (
    <form ref={guardRef} className="fin-add" aria-label={label} onSubmit={submit}>
      <label className="field">
        <span>Nazwa</span>
        <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="np. czynsz magazynu" autoComplete="off" />
      </label>
      <label className="field">
        <span>Kwota (zł)</span>
        <NumberInput kind="money" value={d.amount} onValueChange={(amount) => setD({ ...d, amount })} />
      </label>
      <label className="field">
        <span>Okres</span>
        <select value={d.period} onChange={(e) => setD({ ...d, period: e.target.value as FixedPeriod })}>
          {(Object.keys(PERIODS) as FixedPeriod[]).map((p) => (
            <option key={p} value={p}>
              {PERIODS[p]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{d.period === 'jednorazowo' ? 'Miesiąc' : 'Od'}</span>
        <input type="month" value={d.fromMonth} onChange={(e) => setD({ ...d, fromMonth: e.target.value })} />
      </label>
      {d.period !== 'jednorazowo' && (
        <label className="field">
          <span>Do (puste = nadal)</span>
          <input type="month" value={d.toMonth} onChange={(e) => setD({ ...d, toMonth: e.target.value })} />
        </label>
      )}
      {hints.length > 0 && (
        <fieldset className="fin-hints">
          <legend>Dopasowanie przelewów (opcjonalnie)</legend>
          {hints.map((h) => (
            <label key={h.key} className="field">
              <span>{h.label}</span>
              <input value={d[h.key]} onChange={(e) => setD({ ...d, [h.key]: e.target.value })} placeholder={h.placeholder} autoComplete="off" />
            </label>
          ))}
        </fieldset>
      )}
      <button className="btn primary" type="submit" disabled={pending}>
        {onCancel ? 'Zapisz' : 'Dodaj koszt stały'}
      </button>
      {onCancel && (
        <button className="btn ghost" type="button" onClick={onCancel}>
          Anuluj
        </button>
      )}
      {message && (
        <p className="alert" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}

const periodText = (c: FixedCost) =>
  c.period === 'jednorazowo' ? monthLabel(c.fromMonth) : `${PERIODS[c.period].toLowerCase()} · od ${monthLabel(c.fromMonth)}${c.toMonth ? ` do ${monthLabel(c.toMonth)}` : ''}${c.period === 'rocznie' ? ` (w ${monthLabel(c.fromMonth).split(' ')[0]})` : ''}`;

function FixedCosts({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: [...FINANCE_KEY, 'fixed'], queryFn: () => api<{ fixedCosts: FixedCost[] }>('/api/finance/fixed-costs') });
  const [editing, setEditing] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: FINANCE_KEY });
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/finance/fixed-costs', { method: 'POST', body }),
    onSuccess: () => {
      setFormKey((k) => k + 1);
      refresh();
    },
  });
  const update = useMutation({
    mutationFn: ({ c, body }: { c: FixedCost; body: Record<string, unknown> }) => api(`/api/finance/fixed-costs/${c.id}`, { method: 'PATCH', body: { ...body, version: c.version } }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (c: FixedCost) => api(`/api/finance/fixed-costs/${c.id}?version=${c.version}`, { method: 'DELETE' }), onSettled: refresh });
  const rows = list.data?.fixedCosts ?? [];
  const mayEdit = can(me, FIXED_RESOURCE, 'edit');
  const mayDelete = can(me, FIXED_RESOURCE, 'delete');
  return (
    <section aria-label="Koszty stałe" data-feature-id="finance.fixed_costs">
      <p className="lede">Stałe wydatki firmy odejmowane w zestawieniach: co miesiąc, raz w roku (w miesiącu „od”) albo jednorazowo. Status (opłacone / nieopłacone / nieznany) bierze się z przelewów z wyciągów — kliknij nazwę, żeby zobaczyć wcześniejsze okresy.</p>
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać kosztów stałych')}</p>}
      {list.data && rows.length === 0 && <p className="muted">Nie ma jeszcze kosztów stałych.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table fin-table">
            <thead>
              <tr>
                <th scope="col">Nazwa</th>
                <th scope="col">Okres</th>
                <th scope="col" className="num">Kwota</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) =>
                editing === c.id ? (
                  <tr key={c.id}>
                    <td colSpan={5}>
                      {update.error instanceof ApiError && update.error.status === 409 && (
                        <ConflictNotice
                          onReload={() => {
                            update.reset();
                            setEditing(null);
                            refresh();
                          }}
                        />
                      )}
                      <FixedForm me={me} label={`Zmiana: ${c.name}`} initial={draftOf(c)} pending={update.isPending} error={update.error instanceof ApiError && update.error.status === 409 ? null : update.error} onCancel={() => setEditing(null)} onSave={(body) => update.mutateAsync({ c, body })} />
                    </td>
                  </tr>
                ) : (
                  <Fragment key={c.id}>
                    <tr>
                      <td>
                        {/* owner 2026-10-24: the cost's details — its periods with their statuses */}
                        <button className="link-button" type="button" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)}>
                          {c.name}
                        </button>
                      </td>
                      <td>{periodText(c)}</td>
                      <td className="num">{formatPln(c.amountGrosze)}</td>
                      <td>{c.current ? <PaymentLabel o={c.current} /> : <span className="muted">—</span>}</td>
                      <td className="q-actions">
                        {mayEdit && (
                          <button className="btn small ghost" type="button" onClick={() => leaveGuarded(() => setEditing(c.id))}>
                            Zmień
                          </button>
                        )}
                        {mayDelete && (
                          <button className="btn icon ghost" type="button" aria-label={`Usuń koszt stały: ${c.name}`} title="Usuń" onClick={() => remove.mutate(c)}>
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                    {open === c.id && (
                      <tr className="fixed-details">
                        <td colSpan={5}>
                          <FixedCostPeriods cost={c} me={me} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
      {remove.error && <p className="alert" role="alert">{errorText(remove.error, 'Nie udało się usunąć')}</p>}
      {can(me, FIXED_RESOURCE, 'create') && <FixedForm me={me} key={formKey} label="Nowy koszt stały" initial={emptyDraft()} pending={create.isPending} error={create.error} onSave={(body) => create.mutateAsync(body)} />}
    </section>
  );
}

// ---------- Ustawienia: transport własny (owner 2026-10-24: z cen paliw) → TransportSettings ----------

/**
 * The F29 tabs of "Finanse" (F32: Podsumowanie, Koszty stałe, Ustawienia); `FinanceLayout` shows the heading and the
 * tabs, and each route checks its own right.
 */
export function FinancePage({ segment }: { segment: Segment }) {
  const { me } = useMe();
  if (!me) return null;
  return (
    <section className="page wide finance-page">
      {segment === 'zestawienie' && <SummaryPage me={me} />}
      {segment === 'koszty-stale' && <FixedCosts me={me} />}
      {segment === 'ustawienia' && (
        <>
          {/* F34: the company data and numbering of invoices */}
          {can(me, INVOICE_SETTINGS_RESOURCE, 'view') && <InvoiceSettingsPage me={me} />}
          {/* owner 2026-10-20 23:50: the wording of invoice lines by what the quote contains */}
          {can(me, INVOICE_SETTINGS_RESOURCE, 'view') && <LineRulesSettings me={me} />}
          {can(me, SETTINGS_RESOURCE, 'view') && <TransportSettings me={me} />}
          {/* F33: the KSeF state (mode, queue, last connection) */}
          {can(me, KSEF_STATUS_RESOURCE, 'view') && <KsefStatusCard isOwner={me.isOwner} />}
          {can(me, SETTLEMENTS_SETTINGS_RESOURCE, 'view') && <SettlementsSettings me={me} />}
          {/* F36: reminders of overdue receivables */}
          {can(me, PAYMENTS_SETTINGS_RESOURCE, 'view') && <PaymentsSettings me={me} />}
        </>
      )}
    </section>
  );
}
